import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Grid,
  Chip,
  Snackbar,
  IconButton,
  Tooltip,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  ContentCopy as DuplicateIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import DataTable from '../components/ui/DataTable/DataTable';
import api from '../utils/api';

const ScheduledClassListPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  
  // Status colors
  const getStatusColor = (status) => {
    switch(status?.toLowerCase()) {
      case 'scheduled': return 'primary';
      case 'in progress': return 'secondary';
      case 'completed': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };
  
  const columns = [
    { 
      key: 'title', 
      title: t('admin:scheduledClasses.className'), 
      width: 200,
      render: (value, row) => (
        <Box sx={{ fontWeight: 'medium' }}>{value}</Box>
      )
    },
    { 
      key: 'classType', 
      title: t('admin:scheduledClasses.classType'), 
      width: 150,
      render: (value, row) => (
        <Chip 
          label={row.classType?.name || t('common:labels.na')}
          size="small"
          variant="outlined"
          color="primary"
        />
      )
    },
    { 
      key: 'trainer', 
      title: t('admin:scheduledClasses.trainer'), 
      width: 150,
      render: (value, row) => {
        const trainer = row.trainer;
        return trainer ? (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PersonIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
            {`${trainer.firstName} ${trainer.lastName}`}
          </Box>
        ) : t('common:labels.na');
      }
    },
    { 
      key: 'facility', 
      title: t('admin:scheduledClasses.facility'), 
      width: 150,
      render: (value, row) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <LocationIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
          {row.facility?.name || t('common:labels.na')}
        </Box>
      )
    },
    { 
      key: 'startTime', 
      title: t('admin:scheduledClasses.dateTime'), 
      width: 200,
      render: (value, row) => {
        if (!value) return t('common:labels.na');
        const date = new Date(value);
        return (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
              <CalendarIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
              {format(date, 'MMM dd, yyyy')}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <TimeIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
              {format(date, 'h:mm a')} - {row.endTime ? format(new Date(row.endTime), 'h:mm a') : t('common:labels.na')}
            </Box>
          </Box>
        );
      }
    },
    { 
      key: 'capacity', 
      title: t('common:labels.capacity'), 
      width: 120,
      render: (value, row) => (
        <Box>
          <Typography variant="body2">
            {row.enrolledCount || 0} / {value || 0}
          </Typography>
          <Box 
            sx={{
              width: '100%',
              height: 4,
              bgcolor: 'grey.200',
              borderRadius: 2,
              mt: 0.5,
              overflow: 'hidden'
            }}
          >
            <Box 
              sx={{
                width: `${row.enrolledCount && value ? (row.enrolledCount / value) * 100 : 0}%`,
                height: '100%',
                bgcolor: row.enrolledCount >= value ? 'error.main' : 'primary.main',
                borderRadius: 2
              }}
            />
          </Box>
        </Box>
      )
    },
    { 
      key: 'status', 
      title: t('common:labels.status'), 
      width: 120,
      render: (value) => (
        <Chip 
          label={value || t('common:labels.na')}
          size="small"
          color={getStatusColor(value)}
        />
      )
    },
    {
      key: 'actions',
      title: t('common:labels.actions'),
      width: 160,
      render: (value, row) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title={t('admin:scheduledClasses.actions.viewDetails')}>
            <IconButton
              size="small"
              onClick={() => navigate(`/classes/${row._id || row.id}`)}
              color="info"
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('admin:scheduledClasses.actions.edit')}>
            <IconButton
              size="small"
              onClick={() => navigate(`/admin/scheduled-classes/edit/${row._id || row.id}`)}
              color="primary"
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('admin:scheduledClasses.actions.duplicate')}>
            <IconButton
              size="small"
              onClick={() => navigate('/admin/scheduled-classes/add', {
                state: { duplicateData: row }
              })}
              color="secondary"
            >
              <DuplicateIcon />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  useEffect(() => {
    fetchClasses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  const fetchClasses = async () => {
    try {
      setLoading(true);
      const response = await api.get('/scheduled-classes', {
        params: {
          page,
          limit: pageSize
        }
      });

      // Map API data to the format expected by the table
      const mappedClasses = (response.data.data || []).map(cls => ({
        ...cls,
        id: cls._id,
        title: cls.classType?.name || t('admin:scheduledClasses.unnamedClass'),
        enrolledCount: cls.currentEnrollment || 0,
        status: cls.status || 'scheduled'
      }));
      setClasses(mappedClasses);
      setTotalCount(response.data.pagination?.total || 0);
      setError('');
    } catch (err) {
      console.error('Error fetching scheduled classes:', err);
      setError(t('admin:scheduledClasses.errors.loadFailed'));
      setClasses([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };
  
  
  
  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };
  

  // Configure DataTable props
  const tableConfig = {
    data: classes,
    columns: columns,
    loading: loading,
    error: error,
    pagination: {
      enabled: true,
      page: page,
      pageSize: pageSize,
      total: totalCount,
      onPageChange: (newPage) => setPage(newPage),
      onPageSizeChange: (newPageSize) => {
        setPageSize(newPageSize);
        setPage(1); // Reset to first page when page size changes
      }
    },
    sorting: {
      enabled: true,
      defaultKey: 'startTime',
      defaultDirection: 'asc'
    },
    selection: {
      enabled: false
    },
    actions: {
      enabled: true,
      items: [
        {
          label: t('common:actions.viewDetails'),
          onClick: (row) => console.log('View details', row)
        },
        {
          label: t('common:buttons.edit'),
          onClick: (row) => console.log('Edit', row)
        },
        {
          label: t('admin:scheduledClasses.actions.cancelClass'),
          onClick: (row) => console.log('Cancel', row),
          color: 'error'
        }
      ]
    },
    emptyState: {
      message: t('admin:scheduledClasses.emptyState.title'),
      description: t('admin:scheduledClasses.emptyState.description')
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
          {t('admin:scheduledClasses.title')}
        </Typography>
        <Box>
          <Tooltip title={t('common:buttons.refresh')}>
            <IconButton onClick={fetchClasses} sx={{ mr: 1 }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => navigate('/admin/scheduled-classes/add')}
          >
            {t('admin:scheduledClasses.addButton')}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ width: '100%', overflow: 'hidden', borderRadius: 2, boxShadow: 2 }}>
        <DataTable {...tableConfig} />
      </Paper>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ScheduledClassListPage;
